import { DiagramElements } from "canvas/elements";
import { arrangeDiagram } from "diagram/arrange/arrange";
import { EditDiagram } from "diagram/model/edit/edit";
import { parseDiagram } from "diagram/model/parse/parse";
import { DiagramProps } from "diagram/state/state";
import { addHostCallbackFuture } from "host/callbacks";
import { LibraryProps } from "library/library";
import { LibraryReducer } from "library/reducer";
import React from "react";
import { flushSync } from "react-dom";

const registerJson = addHostCallbackFuture("importDiagramJSON");
const registerObject = addHostCallbackFuture("importDiagramObject");
const registerBase64 = addHostCallbackFuture("importDiagramBase64");

interface Props {
    savedDiagram: React.MutableRefObject<EditDiagram | null>;
    libraryDiagramRef: React.MutableRefObject<EditDiagram | null>;
    elements: DiagramElements;
    diagramDispatch: DiagramProps["diagramDispatch"];
    hostJustImportedRef: React.MutableRefObject<boolean>;
    setLibrary: LibraryProps["setLibrary"];
}

/** Plugin payloads with inconsistent FK schema/table names often throw in makeRefSide; retry without refs. */
const parseDiagramWithRefsFallback = (source: unknown): EditDiagram => {
    try {
        return parseDiagram(source);
    } catch (first) {
        if (
            source !== null &&
            typeof source === "object" &&
            !Array.isArray(source) &&
            "items" in source
        ) {
            const o = source as Record<string, unknown>;
            const retry = { ...o, refs: [] };
            try {
                const diagram = parseDiagram(retry);
                return diagram;
            } catch {
                /* use original error */
            }
        }
        throw first;
    }
};

export const useDiagramHostCallbacks = (props: Props) => {
    const {
        savedDiagram,
        libraryDiagramRef,
        elements,
        diagramDispatch,
        hostJustImportedRef,
        setLibrary,
    } = props;
    React.useEffect(() => {
        const importDiagram = (source: unknown): void => {
            (async () => {
                try {
                    hostJustImportedRef.current = true;
                    await new Promise((r) => window.setTimeout(r, 100));
                    let diagram = parseDiagramWithRefsFallback(source);
                    savedDiagram.current = diagram;
                    flushSync(() => {
                        diagramDispatch({
                            type: "replace-as-is",
                            payload: { diagram },
                        });
                    });
                    const expectedCount = diagram.tables.length;
                    const layoutWaitStart = Date.now();
                    const deadline = layoutWaitStart + 5000;
                    while (
                        elements.tables.size < expectedCount &&
                        Date.now() < deadline
                    ) {
                        await new Promise((r) => window.setTimeout(r, 50));
                    }
                    if (elements.tables.size >= expectedCount) {
                        await new Promise((r) => window.setTimeout(r, 50));
                        diagram = arrangeDiagram(elements, diagram);
                        savedDiagram.current = diagram;
                        flushSync(() => {
                            diagramDispatch({
                                type: "replace-as-is",
                                payload: { diagram },
                            });
                        });
                    }
                    libraryDiagramRef.current = diagram;
                    setLibrary((prev) => {
                        if (prev.current === null) return prev;
                        return LibraryReducer.set(prev.current, diagram)(prev);
                    });
                } catch {
                    /* host import failed */
                } finally {
                    await new Promise<void>((resolve) => {
                        requestAnimationFrame(() => {
                            requestAnimationFrame(() => resolve());
                        });
                    });
                    hostJustImportedRef.current = false;
                }
            })();
        };
        // Having 3 separated methods is only for backware-compatibility. The
        // importDiagram can handle any format
        registerJson(importDiagram);
        registerObject(importDiagram);
        registerBase64(importDiagram);
    }, [
        diagramDispatch,
        elements,
        savedDiagram,
        libraryDiagramRef,
        hostJustImportedRef,
        setLibrary,
    ]);
};
